import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import * as RadixTabs from "@radix-ui/react-tabs";
import { useMsal } from "@azure/msal-react";
import { fetchResortsOverview, fetchStudyStatus, fetchToDo } from "../../api/statisticsApi";
import { cn } from "../../lib/utils";
import { ToDoTab } from "./ToDoTab";
import { ResortsTab } from "./ResortsTab";
import { StudiesTab } from "./StudiesTab";
import { shortEmail } from "./statsUi";

const TAB_TRIGGER = cn(
  "px-4 py-2 text-[13px] font-medium text-[var(--sc-muted)] border-b-2 border-transparent -mb-px",
  "transition-colors cursor-pointer bg-transparent whitespace-nowrap",
  "hover:text-[var(--sc-text)]",
  "data-[state=active]:text-[var(--sc-text)] data-[state=active]:border-[var(--sc-amber)]",
  "focus-visible:outline-none"
);

const SELECT = cn(
  "rounded-lg border border-[var(--sc-border)] px-3 py-1.5 text-sm text-[var(--sc-text)] bg-[var(--sc-surface)]",
  "focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
);

const LABEL = "text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)]";

const TABS = [
  { id: "todo", label: "Do zrobienia" },
  { id: "resorts", label: "Ośrodki" },
  { id: "studies", label: "Rekrutacja i bezpieczeństwo" },
];

export const StatisticsPage = () => {
  const { instance } = useMsal();
  const currentUserEmail = instance.getActiveAccount()?.username ?? "";
  const [tab, setTab] = useState("todo");
  const [study, setStudy] = useState("");
  const [monitor, setMonitor] = useState("");

  const toDo = useQuery(["statistics-todo"], fetchToDo);
  const resorts = useQuery(["statistics-resorts"], fetchResortsOverview);
  const studies = useQuery(["statistics-studies"], fetchStudyStatus);

  const studyNames = useMemo(() => {
    const names = new Set<string>();
    toDo.data?.visitsWithoutReport.forEach(v => names.add(v.studyName));
    toDo.data?.draftReports.forEach(r => names.add(r.studyName));
    toDo.data?.reviewQueue.forEach(r => names.add(r.studyName));
    resorts.data?.forEach(r => names.add(r.studyName));
    studies.data?.forEach(s => names.add(s.studyName));
    return Array.from(names).filter(Boolean).sort((a, b) => a.localeCompare(b, "pl"));
  }, [toDo.data, resorts.data, studies.data]);

  const monitors = useMemo(() => {
    const emails = new Set<string>();
    toDo.data?.visitsWithoutReport.forEach(v => v.monitors.forEach(m => emails.add(m)));
    toDo.data?.draftReports.forEach(r => emails.add(r.author));
    toDo.data?.reviewQueue.forEach(r => emails.add(r.author));
    return Array.from(emails).filter(Boolean).sort((a, b) => a.localeCompare(b, "pl"));
  }, [toDo.data]);

  return (
    <div className="flex-1 overflow-y-auto bg-[var(--sc-bg)] px-4 md:px-12 py-9">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[28px] font-light tracking-tight text-[var(--sc-text)] leading-none">Statystyki</h1>
          <p className="text-[13px] text-[var(--sc-muted)] mt-1">
            Stan na dziś · bez badań testowych i szablonów
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className={LABEL}>Badanie:</label>
            <select value={study} onChange={e => setStudy(e.target.value)} className={SELECT}>
              <option value="">Wszystkie</option>
              {studyNames.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {tab === "todo" && (
            <div className="flex items-center gap-2">
              <label className={LABEL}>Monitor:</label>
              <select value={monitor} onChange={e => setMonitor(e.target.value)} className={SELECT}>
                <option value="">Wszyscy</option>
                {monitors.map(m => (
                  <option key={m} value={m}>{shortEmail(m)}{m === currentUserEmail ? " (ja)" : ""}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <RadixTabs.Root value={tab} onValueChange={setTab}>
        <RadixTabs.List className="flex border-b border-[var(--sc-border)] bg-transparent gap-0 mb-6 overflow-x-auto">
          {TABS.map(t => (
            <RadixTabs.Trigger key={t.id} value={t.id} className={TAB_TRIGGER}>{t.label}</RadixTabs.Trigger>
          ))}
        </RadixTabs.List>

        <RadixTabs.Content value="todo" className="focus-visible:outline-none">
          <ToDoTab data={toDo.data} isLoading={toDo.isLoading} study={study} monitor={monitor} />
        </RadixTabs.Content>
        <RadixTabs.Content value="resorts" className="focus-visible:outline-none">
          <ResortsTab data={resorts.data} isLoading={resorts.isLoading} study={study} />
        </RadixTabs.Content>
        <RadixTabs.Content value="studies" className="focus-visible:outline-none">
          <StudiesTab data={studies.data} isLoading={studies.isLoading} study={study} />
        </RadixTabs.Content>
      </RadixTabs.Root>
    </div>
  );
};
